import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import "./SubscriptionSelection.css";

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  max_cases_per_month: number | null;
  max_intake_forms_per_month: number | null;
  max_summaries_per_month: number | null;
  max_auto_fills_per_month: number | null;
  max_storage_gb: number | null;
  features: string[];
  is_trial: boolean;
  is_popular: boolean;
  is_developer_only: boolean;
}

interface SubscriptionSelectionProps {
  onSubscriptionComplete: () => void;
  isNewUser?: boolean;
  onBackToHome?: () => void;
}

const SubscriptionSelection: React.FC<SubscriptionSelectionProps> = ({
  onSubscriptionComplete,
  isNewUser = false,
  onBackToHome,
}) => {
  const { signOut } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPlans();
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get("/subscriptions/plans");
      setPlans(response);

      // Auto-select free trial for new users
      if (isNewUser) {
        const trialPlan = response.find((p: SubscriptionPlan) => p.is_trial);
        if (trialPlan) {
          setSelectedPlan(trialPlan.id);
        }
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setError("Failed to load subscription plans");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSelectPlan = async () => {
    if (!selectedPlan) {
      setError("Please select a plan");
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Try to create subscription
      await apiClient.post("/subscriptions/create", {
        planId: selectedPlan,
        billingCycle: billingCycle,
      });

      // Success! Free trial created (old flow - no card required), redirect to dashboard
      onSubscriptionComplete();
    } catch (error: any) {
      console.error("Error creating subscription:", error);

      // Check if payment method is required (402 status)
      if (
        error.response?.status === 402 &&
        (error.response?.data?.requiresPayment || error.response?.data?.requiresPaymentMethod)
      ) {
        // Redirect to Stripe checkout (works for both trials and paid plans)
        try {
          const isTrial = error.response?.data?.isTrial || false;
          
          const checkoutResponse = await apiClient.post(
            "/stripe/create-checkout-session",
            {
              planId: error.response.data.planId,
              billingCycle: billingCycle,
              isTrial: isTrial,
            }
          );

          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.url;
        } catch (checkoutError: any) {
          console.error("Error creating checkout session:", checkoutError);
          setError("Failed to initiate payment. Please try again.");
          setProcessing(false);
        }
      } else if (error.response?.status === 403 && error.response?.data?.trialAlreadyUsed) {
        // Trial already used
        setError(error.response.data.message || "You have already used your free trial.");
        setProcessing(false);
      } else {
        setError(
          error.response?.data?.error || "Failed to create subscription"
        );
        setProcessing(false);
      }
    }
  };

  const formatPrice = (plan: SubscriptionPlan) => {
    if (plan.is_trial || plan.price_monthly === 0) {
      return "Free";
    }

    const price =
      billingCycle === "monthly" ? plan.price_monthly : plan.price_yearly / 12;
    return `$${price.toFixed(0)} USD/month`;
  };
  
  const handleBackToHome = async () => {
    if (onBackToHome) {
      onBackToHome();
    } else {
      // If no callback provided, sign out user to return to landing page
      await signOut();
    }
  };

  const formatLimit = (limit: number | null) => {
    return limit === null ? "Unlimited" : limit.toString();
  };

  if (initialLoading && plans.length === 0) {
    return (
      <div className="subscription-selection-container">
        <div className="loading-spinner">Loading subscription plans...</div>
      </div>
    );
  }

  return (
    <div className="subscription-selection-container">
      <div className="subscription-header">
        <button 
          className="back-to-home-btn"
          onClick={handleBackToHome}
          title="Back to Home"
        >
          ← Back to Home
        </button>
        <h1>Choose Your Plan</h1>
        <p className="subscription-subtitle">
          Select a subscription plan to access Immilink features
        </p>

        {/* Billing cycle toggle (hide for trial users) */}
        {!isNewUser && (
          <div className="billing-cycle-toggle">
            <button
              className={`cycle-btn ${
                billingCycle === "monthly" ? "active" : ""
              }`}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={`cycle-btn ${
                billingCycle === "yearly" ? "active" : ""
              }`}
              onClick={() => setBillingCycle("yearly")}
            >
              Yearly <span className="save-badge">Save 17%</span>
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="plans-grid">
        {(() => {
          if (!plans || !Array.isArray(plans)) {
            return <div>Error: Plans data is invalid</div>;
          }

          return plans.map((plan) => {

            return (
              <div
                key={plan.id}
                className={`plan-card ${
                  selectedPlan === plan.id ? "selected" : ""
                } ${plan.is_popular ? "popular" : ""}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.is_popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                {plan.is_developer_only && (
                  <div className="developer-badge">Development Only</div>
                )}

                <div className="plan-header">
                  <h3 className="plan-name">{plan.display_name}</h3>
                  <div className="plan-price">
                    <span className="price">{formatPrice(plan)}</span>
                    {!plan.is_trial &&
                      plan.price_monthly > 0 &&
                      billingCycle === "yearly" && (
                        <span className="price-note">
                          Billed ${plan.price_yearly}/year
                        </span>
                      )}
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="plan-features">
                  <div className="feature-item">
                    <span className="feature-icon">📝</span>
                    <span>
                      {formatLimit(plan.max_summaries_per_month)} AI
                      summaries/month
                    </span>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <div className="additional-features">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="feature-item">
                          <span className="feature-icon">✓</span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  className={`select-plan-btn ${
                    selectedPlan === plan.id ? "selected" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlan(plan.id);
                  }}
                >
                  {selectedPlan === plan.id ? "✓ Selected" : "Select Plan"}
                </button>
              </div>
            );
          });
        })()}
      </div>

      <div className="subscription-actions">
        <button
          className="continue-btn"
          onClick={handleSelectPlan}
          disabled={!selectedPlan || processing}
        >
          {processing ? "Processing..." : "Continue to Dashboard"}
        </button>
        <p className="terms-note">
          By continuing, you agree to our{' '}
          <a href="/terms-of-service">Terms of Service</a>{' '}
          and{' '}
          <a href="/privacy-policy">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSelection;
